import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type SiteAdminsUpdate = Database['public']['Tables']['site_admins']['Update'];
type RealtorProfilesInsert = Database['public']['Tables']['realtor_profiles']['Insert'];
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Home, ArrowLeft, Camera, Upload, Loader2, Settings, Sparkles } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SiteSwitcher } from '@/components/admin/SiteSwitcher';
import { RealtorAdManager } from '@/components/admin/RealtorAdManager';
import { SiteAdManager } from '@/components/admin/SiteAdManager';

interface ProfileData {
  id: string;
  site_id: string;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  logo_url: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  license_numbers: string | null;
  about_me: string | null;
  title_preference: 'agent' | 'realtor';
  ad_image_url: string | null;
}

export default function AdminProfile() {
  const { user, loading: authLoading } = useAuth();
  const { isGlobalAdmin, selectedSiteId, loading: globalAdminLoading } = useGlobalAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [realtorProfile, setRealtorProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [generatingAbout, setGeneratingAbout] = useState(false);
  const [bioContext, setBioContext] = useState('');
  const [siteName, setSiteName] = useState<string>('');
  const [siteType, setSiteType] = useState<string>('agent');

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    license_numbers: '',
    about_me: '',
    title_preference: 'agent' as 'agent' | 'realtor',
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !globalAdminLoading) {
      fetchProfile();
    }
  }, [user, globalAdminLoading, selectedSiteId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      // For global admins viewing a selected site, load from realtor_profiles
      if (isGlobalAdmin && selectedSiteId) {
        // Get site name and type for display
        const { data: siteData } = await supabase
          .from('sites')
          .select('name, site_type')
          .eq('id', selectedSiteId)
          .single();
        
        if (siteData) {
          setSiteName(siteData.name);
          setSiteType(siteData.site_type || 'agent');
        }

        // Get realtor profile for this site
        const { data: realtorData, error: realtorError } = await supabase
          .from('realtor_profiles')
          .select('*')
          .eq('site_id', selectedSiteId)
          .maybeSingle();

        if (realtorError) throw realtorError;

        if (realtorData) {
          setRealtorProfile(realtorData);
          setProfile({
            id: realtorData.id,
            site_id: selectedSiteId,
            first_name: realtorData.first_name,
            last_name: realtorData.last_name,
            photo_url: realtorData.photo_url,
            logo_url: realtorData.logo_url,
            phone: realtorData.phone,
            address: realtorData.address,
            city: realtorData.city,
            state: realtorData.state,
            zip: realtorData.zip,
            license_numbers: realtorData.license_numbers,
            about_me: realtorData.about_me,
            title_preference: (realtorData.title_preference as 'agent' | 'realtor') || 'agent',
            ad_image_url: realtorData.ad_image_url,
          });
          setFormData({
            first_name: realtorData.first_name || '',
            last_name: realtorData.last_name || '',
            phone: realtorData.phone || '',
            address: realtorData.address || '',
            city: realtorData.city || '',
            state: realtorData.state || '',
            zip: realtorData.zip || '',
            license_numbers: realtorData.license_numbers || '',
            about_me: realtorData.about_me || '',
            title_preference: (realtorData.title_preference as 'agent' | 'realtor') || 'agent',
          });
        } else {
          // No realtor profile exists yet - create empty state
          setProfile({
            id: '',
            site_id: selectedSiteId,
            first_name: null,
            last_name: null,
            photo_url: null,
            logo_url: null,
            phone: null,
            address: null,
            city: null,
            state: null,
            zip: null,
            license_numbers: null,
            about_me: null,
            title_preference: 'agent',
            ad_image_url: null,
          });
          setFormData({
            first_name: '',
            last_name: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            zip: '',
            license_numbers: '',
            about_me: '',
            title_preference: 'agent',
          });
        }
      } else {
        // Regular site admin - load their own profile
        const { data, error } = await supabase
          .from('site_admins')
          .select('id, site_id, first_name, last_name, photo_url, logo_url, phone, address, city, state, zip, license_numbers, about_me')
          .eq('user_id', user!.id)
          .single();

        if (error) throw error;

        if (data) {
          // Fetch site type
          const { data: siteData } = await supabase
            .from('sites')
            .select('site_type')
            .eq('id', data.site_id)
            .single();
          
          if (siteData) {
            setSiteType(siteData.site_type || 'agent');
          }
          
          // Also fetch the realtor_profile for title_preference and ad_image_url
          const { data: realtorData } = await supabase
            .from('realtor_profiles')
            .select('id, title_preference, ad_image_url')
            .eq('site_id', data.site_id)
            .maybeSingle();
          
          setProfile({
            ...data,
            title_preference: (realtorData?.title_preference as 'agent' | 'realtor') || 'agent',
            ad_image_url: realtorData?.ad_image_url || null,
          });
          setFormData({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            phone: data.phone || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            zip: data.zip || '',
            license_numbers: data.license_numbers || '',
            about_me: data.about_me || '',
            title_preference: (realtorData?.title_preference as 'agent' | 'realtor') || 'agent',
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!profile || !profile.site_id) return;

    setSaving(true);
    try {
      // For global admins, only update realtor_profiles (they don't have site_admin records for other sites)
      if (isGlobalAdmin && selectedSiteId) {
        const { error: profileError } = await supabase
          .from('realtor_profiles')
          .upsert({
            site_id: profile.site_id,
            first_name: formData.first_name || null,
            last_name: formData.last_name || null,
            phone: formData.phone || null,
            address: formData.address || null,
            city: formData.city || null,
            state: formData.state || null,
            zip: formData.zip || null,
            license_numbers: formData.license_numbers || null,
            about_me: formData.about_me || null,
            title_preference: formData.title_preference,
          }, { onConflict: 'site_id' });

        if (profileError) throw profileError;
      } else {
        // Regular site admin - update both tables
        const { error: adminError } = await supabase
          .from('site_admins')
          .update({
            first_name: formData.first_name || null,
            last_name: formData.last_name || null,
            phone: formData.phone || null,
            address: formData.address || null,
            city: formData.city || null,
            state: formData.state || null,
            zip: formData.zip || null,
            license_numbers: formData.license_numbers || null,
            about_me: formData.about_me || null,
          })
          .eq('id', profile.id);
        if (adminError) throw adminError;

        // Also update realtor_profiles (public display data)
        const { error: profileError } = await supabase
          .from('realtor_profiles')
          .upsert({
            site_id: profile.site_id,
            first_name: formData.first_name || null,
            last_name: formData.last_name || null,
            phone: formData.phone || null,
            address: formData.address || null,
            city: formData.city || null,
            state: formData.state || null,
            zip: formData.zip || null,
            license_numbers: formData.license_numbers || null,
            about_me: formData.about_me || null,
            title_preference: formData.title_preference,
          }, { onConflict: 'site_id' });

        if (profileError) {
          console.error('Error syncing realtor profile:', profileError);
          // Non-blocking - still show success for admin save
        }
      }

      toast({
        title: 'Profile saved',
        description: 'Your profile has been updated successfully.',
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAbout = async () => {
    if (!formData.first_name) {
      toast({
        title: 'First name required',
        description: 'Please enter a first name before generating.',
        variant: 'destructive',
      });
      return;
    }

    setGeneratingAbout(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-about-me', {
        body: {
          firstName: formData.first_name,
          lastName: formData.last_name,
          city: formData.city,
          state: formData.state,
          licenseNumbers: formData.license_numbers,
          additionalContext: bioContext,
        },
      });

      if (error) throw error;

      if (data?.aboutMe) {
        setFormData(prev => ({ ...prev, about_me: data.aboutMe }));
        toast({
          title: 'Bio generated',
          description: 'Review the generated bio and save when ready.',
        });
      }
    } catch (error) {
      console.error('Error generating about me:', error);
      toast({
        title: 'Generation failed',
        description: 'Failed to generate bio. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setGeneratingAbout(false);
    }
  };

  const handleImageUpload = async (file: File, type: 'photo' | 'logo') => {
    if (!user || !profile) return;

    const isPhoto = type === 'photo';
    isPhoto ? setUploadingPhoto(true) : setUploadingLogo(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${type}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('realtor-assets')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('realtor-assets')
        .getPublicUrl(fileName);

      const updateField = isPhoto ? 'photo_url' : 'logo_url';
      
      // Update site_admins
      const { error: updateError } = await supabase
        .from('site_admins')
        .update({ [updateField]: publicUrl } as SiteAdminsUpdate)
        .eq('id', profile.id);

      if (updateError) throw updateError;

      // Also sync to realtor_profiles (public display)
      await supabase
        .from('realtor_profiles')
        .upsert({
          site_id: profile.site_id,
          [updateField]: publicUrl,
        } as RealtorProfilesInsert, { onConflict: 'site_id' });

      setProfile(prev => prev ? { ...prev, [updateField]: publicUrl } : null);
      
      toast({
        title: `${isPhoto ? 'Photo' : 'Logo'} uploaded`,
        description: `Your ${type} has been updated successfully.`,
      });
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      toast({
        title: 'Upload failed',
        description: `Failed to upload ${type}. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      isPhoto ? setUploadingPhoto(false) : setUploadingLogo(false);
    }
  };

  if (authLoading || globalAdminLoading || loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Home className="h-6 w-6 text-primary" />
              <span className="font-display text-xl">
                {isGlobalAdmin ? 'Realtor Profile' : 'My Profile'}
              </span>
              {isGlobalAdmin && <SiteSwitcher />}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')}>
                <Settings className="h-4 w-4 mr-2" />
                Site Settings
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold">
            {isGlobalAdmin && siteName ? `${siteName} - Realtor Card` : 'Profile Settings'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isGlobalAdmin 
              ? 'Edit the realtor information displayed on the public website.' 
              : 'Update your profile information that appears on your website.'}
          </p>
        </div>

        {/* Photo & Logo */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{siteType === 'community' ? 'Logo' : 'Photo & Logo'}</CardTitle>
            <CardDescription>
              {siteType === 'community' 
                ? 'Upload your community or organization logo'
                : 'Upload your professional photo and business logo'}
            </CardDescription>
          </CardHeader>
          <CardContent className={siteType === 'community' ? '' : 'grid md:grid-cols-2 gap-6'}>
            {/* Photo Upload - Hidden for community sites */}
            {siteType !== 'community' && (
              <div>
                <Label className="mb-2 block">Profile Photo</Label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-full bg-muted border-2 border-dashed border-border flex items-center justify-center overflow-hidden">
                    {profile?.photo_url ? (
                      <img src={profile.photo_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="photo-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file, 'photo');
                      }}
                      disabled={uploadingPhoto}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('photo-upload')?.click()}
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload Photo
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Logo Upload */}
            <div>
              <Label className="mb-2 block">{siteType === 'community' ? 'Community Logo' : 'Business Logo'}</Label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 bg-muted border-2 border-dashed border-border flex items-center justify-center overflow-hidden rounded-lg">
                  {profile?.logo_url ? (
                    <img src={profile.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <Home className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="logo-upload"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, 'logo');
                    }}
                    disabled={uploadingLogo}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('logo-upload')?.click()}
                    disabled={uploadingLogo}
                  >
                    {uploadingLogo ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Upload Logo
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ad Generator - Realtor Ad - Only for agent/broker sites */}
        {siteType !== 'community' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Marketing Ad</CardTitle>
              <CardDescription>Generate AI-powered buyer/seller focused ads using your profile photo</CardDescription>
            </CardHeader>
            <CardContent>
              <RealtorAdManager
                realtorProfileId={realtorProfile?.id || profile?.id || ''}
                realtorName={`${formData.first_name} ${formData.last_name}`.trim()}
                photoUrl={profile?.photo_url || null}
                logoUrl={profile?.logo_url || null}
                adImageUrl={profile?.ad_image_url || null}
                onAdImageChange={(url) => setProfile(prev => prev ? { ...prev, ad_image_url: url } : null)}
              />
            </CardContent>
          </Card>
        )}

        {/* Market Value Report Ad */}
        {profile?.site_id && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Market Value Report Ad</CardTitle>
              <CardDescription>
                {siteType === 'community' 
                  ? 'Ad shown on seller-focused pages - community-focused design'
                  : 'Ad shown on seller-focused pages - uses your photo for personalization'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SiteAdManager
                siteId={profile.site_id}
                adType="seller"
                photoUrl={siteType === 'community' ? null : (profile?.photo_url || null)}
                logoUrl={profile?.logo_url || null}
                onActiveImageChange={() => {}}
              />
            </CardContent>
          </Card>
        )}

        {/* Community Guide Ad */}
        {profile?.site_id && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Community Guide Ad</CardTitle>
              <CardDescription>
                {siteType === 'community' 
                  ? 'Ad shown on buyer-focused pages - community-focused design'
                  : 'Ad shown on buyer-focused pages - uses your photo for personalization'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SiteAdManager
                siteId={profile.site_id}
                adType="buyer"
                photoUrl={siteType === 'community' ? null : (profile?.photo_url || null)}
                logoUrl={profile?.logo_url || null}
                onActiveImageChange={() => {}}
              />
            </CardContent>
          </Card>
        )}

        {/* Personal Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Your name and contact details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="John"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Smith"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
              />
            </div>
          </CardContent>
        </Card>

        {/* Office Address */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Office Address</CardTitle>
            <CardDescription>Your business location</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="123 Main Street"
              />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Boston"
                />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="MA"
                />
              </div>
              <div>
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  placeholder="02101"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* License & About */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Professional Details</CardTitle>
            <CardDescription>License information and bio</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-2 block">Display Title</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="title_preference"
                    value="agent"
                    checked={formData.title_preference === 'agent'}
                    onChange={(e) => setFormData(prev => ({ ...prev, title_preference: e.target.value as 'agent' | 'realtor' }))}
                    className="w-4 h-4 text-primary"
                  />
                  <span>Agent</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="title_preference"
                    value="realtor"
                    checked={formData.title_preference === 'realtor'}
                    onChange={(e) => setFormData(prev => ({ ...prev, title_preference: e.target.value as 'agent' | 'realtor' }))}
                    className="w-4 h-4 text-primary"
                  />
                  <span>Realtor</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">This controls how your title appears on the website (e.g., "Your Local Agent" vs "Your Local Realtor")</p>
            </div>
            <div>
              <Label htmlFor="license_numbers">License Number(s)</Label>
              <Input
                id="license_numbers"
                name="license_numbers"
                value={formData.license_numbers}
                onChange={handleInputChange}
                placeholder="e.g., RES.0050006, 9587365"
              />
              <p className="text-xs text-muted-foreground mt-1">Separate multiple licenses with commas</p>
            </div>
            <div>
              <Label htmlFor="bio_context" className="mb-1 block">Additional Context for AI</Label>
              <Textarea
                id="bio_context"
                value={bioContext}
                onChange={(e) => setBioContext(e.target.value)}
                placeholder="e.g., 15 years experience, specializes in luxury waterfront properties, fluent in Spanish, former architect..."
                rows={2}
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">Optional details to personalize the generated bio</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label htmlFor="about_me">About Me</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateAbout}
                  disabled={generatingAbout || !formData.first_name}
                  className="h-6 text-xs"
                >
                  {generatingAbout ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3 mr-1" />
                      Generate with AI
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="about_me"
                name="about_me"
                value={formData.about_me}
                onChange={handleInputChange}
                placeholder="Tell clients about yourself, your experience, and why they should work with you..."
                rows={5}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Profile'
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
